import { useAuth } from '../context/AuthContext'
import { useGame } from '../context/GameContext'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { useToast } from '../components/ui/Toaster'
import { 
  Users, Share2, ArrowRight, Copy, Check, 
  Clock, AlertCircle, Link as LinkIcon, Trophy
} from 'lucide-react'
import { clsx } from 'clsx'
import { useState, useEffect } from 'react'

export default function DuelLobby() {
  const { inviteCode } = useParams<{ inviteCode: string }>()
  const { user } = useAuth()
  const { currentDuel, fetchDuel, joinDuel } = useGame()
  const { toast } = useToast()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [joining, setJoining] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (inviteCode) {
      fetchDuel(inviteCode).then(() => setLoading(false))
    }
  }, [inviteCode, fetchDuel])

  const handleJoin = async () => {
    if (!user) {
      navigate('/auth', { state: { redirect: `/duel/${inviteCode}` } })
      return
    }
    
    if (!inviteCode) return
    
    setJoining(true)
    try {
      const { error } = await joinDuel(inviteCode)
      if (error) throw error
      navigate(`/duel/${inviteCode}/play`)
    } catch (err) {
      toast({ type: 'error', title: 'Failed to join', message: err instanceof Error ? err.message : 'Try again' })
    } finally {
      setJoining(false)
    }
  }

  const copyLink = async () => {
    if (!inviteCode) return
    const url = `${window.location.origin}/duel/${inviteCode}`
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    toast({ type: 'success', title: 'Link copied!' })
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950 px-4">
        <Card className="max-w-md w-full text-center p-8">
          <div className="w-12 h-12 mx-auto mb-4 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-surface-600 dark:text-surface-400">Loading duel...</p>
        </Card>
      </div>
    )
  }

  if (!currentDuel) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950 px-4">
        <Card className="max-w-md w-full text-center p-8">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-bold text-surface-900 dark:text-surface-100 mb-2">Duel Not Found</h2>
          <p className="text-surface-500 dark:text-surface-400 mb-6">This invite code is invalid or has expired.</p>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </Card>
      </div>
    )
  }

  const duel = currentDuel
  const isCreator = Boolean(user && duel.creator_id === user.id)
  const isOpponent = Boolean(user && duel.opponent_id === user.id)
  const isParticipant = isCreator || isOpponent
  const expired = new Date(duel.expires_at) < new Date()
  const canJoin = !isParticipant && !duel.opponent_id && duel.status === 'waiting' && !expired

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 lg:py-10 space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-brand-500 to-brand-600 rounded-full text-white font-semibold mb-4">
          <Users className="w-5 h-5" />
          <span>Overlap Duel</span>
        </div>
        <h1 className="text-3xl font-bold text-surface-900 dark:text-surface-100 mb-2">
          {duel.prompt?.spectrum_a_label || 'Daily Overlap'}
        </h1>
        <p className="text-surface-500 dark:text-surface-400">
          {duel.prompt?.spectrum_b_label ? `vs ${duel.prompt.spectrum_b_label}` : 'Challenge a friend!'}
        </p>
      </div>

      {/* Status Card */}
      <Card className={clsx(
        'overflow-hidden',
        isParticipant ? 'ring-2 ring-brand-500' : ''
      )}>
        <CardContent className="pt-6">
          {/* Participants */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <ParticipantCard
              name={duel.creator_username || 'Creator'}
              avatar={duel.creator_username?.[0]?.toUpperCase() || 'C'}
              isYou={isCreator}
              status={isCreator ? 'joined' : 'creator'}
              color="brand"
            />
            <ParticipantCard
              name={duel.opponent_username || (canJoin ? 'Join Duel' : 'Waiting...')}
              avatar={duel.opponent_username?.[0]?.toUpperCase() || '?'}
              isYou={isOpponent}
              status={isOpponent ? 'joined' : duel.opponent_id ? 'joined' : 'waiting'}
              color="purple"
              waiting={!duel.opponent_id}
            />
          </div>

          {/* Duel Info */}
          <div className="space-y-3 p-4 bg-surface-50 dark:bg-surface-900/50 rounded-xl">
            <div className="flex items-center justify-between text-sm">
              <span className="text-surface-500">Status</span>
              <span className={clsx('font-semibold', 
                duel.status === 'waiting' ? 'text-yellow-600' :
                duel.status === 'playing' ? 'text-brand-600' :
                duel.status === 'completed' ? 'text-green-600' :
                'text-red-600'
              )}>
                {duel.status.charAt(0).toUpperCase() + duel.status.slice(1)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-surface-500">Expires</span>
              <span className="font-medium text-surface-900 dark:text-surface-100">
                {formatTimeRemaining(duel.expires_at)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-surface-500">Invite Code</span>
              <div className="flex items-center gap-2">
                <code className="font-mono text-lg font-bold text-brand-600 dark:text-brand-400">{duel.invite_code}</code>
                <Button variant="ghost" size="sm" onClick={copyLink} className="p-1">
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3 pt-4">
            {isParticipant && duel.status === 'waiting' && !duel.opponent_id && (
              <>
                <p className="text-center text-surface-500 text-sm">Share this link with a friend to start the duel!</p>
                <Button variant="secondary" className="w-full" onClick={copyLink}>
                  <Share2 className="w-4 h-4 mr-2" />
                  Copy Invite Link
                </Button>
              </>
            )}

            {canJoin && (
              <Button className="w-full" size="lg" onClick={handleJoin} loading={joining}>
                <Users className="w-4 h-4 mr-2" />
                Join Duel
              </Button>
            )}

            {isParticipant && duel.status === 'playing' && (
              <Button className="w-full" size="lg" onClick={() => navigate(`/duel/${inviteCode}/play`)}>
                <ArrowRight className="w-4 h-4 mr-2" />
                Play Now
              </Button>
            )}

            {isParticipant && duel.status === 'completed' && (
              <Button className="w-full" size="lg" variant="secondary" onClick={() => navigate(`/duel/${inviteCode}/result`)}>
                <Trophy className="w-4 h-4 mr-2" />
                View Results
              </Button>
            )}

            {!isParticipant && duel.opponent_id && (
              <p className="text-center text-surface-500 text-sm">This duel is full. <Button variant="ghost" size="sm" onClick={() => navigate('/')}>Create your own</Button></p>
            )}

            {expired && (
              <p className="text-center text-red-500 text-sm">This duel has expired.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Prompt Preview */}
      {duel.prompt && (
        <Card>
          <CardContent className="pt-6">
            <h3 className="font-semibold text-surface-900 dark:text-surface-100 mb-4">Today's Prompt</h3>
            <div className="space-y-4">
              <PromptPreview 
                label={duel.prompt.spectrum_a_label}
                left={duel.prompt.spectrum_a_left}
                right={duel.prompt.spectrum_a_right}
                avg={duel.prompt.crowd_a_avg ?? undefined}
                color="brand"
              />
              <PromptPreview 
                label={duel.prompt.spectrum_b_label}
                left={duel.prompt.spectrum_b_left}
                right={duel.prompt.spectrum_b_right}
                avg={duel.prompt.crowd_b_avg ?? undefined}
                color="purple"
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

interface ParticipantCardProps {
  name: string
  avatar: string
  isYou: boolean
  status: 'joined' | 'creator' | 'waiting'
  color: 'brand' | 'purple'
  waiting?: boolean
}

function ParticipantCard({ name, avatar, isYou, status, color, waiting }: ParticipantCardProps) {
  const colors = {
    brand: 'bg-gradient-to-br from-brand-500 to-brand-600',
    purple: 'bg-gradient-to-br from-purple-500 to-purple-600',
  }

  const statusColors = {
    joined: 'text-green-600 dark:text-green-400',
    creator: 'text-brand-600 dark:text-brand-400',
    waiting: 'text-yellow-600 dark:text-yellow-400',
  }

  return (
    <div className={clsx(
      'p-4 rounded-xl text-center relative',
      isYou ? 'ring-2 ring-brand-500 bg-brand-50 dark:bg-brand-900/20' : 'bg-surface-50 dark:bg-surface-900/50'
    )}>
      {isYou && (
        <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-brand-500 text-white text-xs rounded-full font-medium">
          You
        </span>
      )}
      {waiting && (
        <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-yellow-500 text-white text-xs rounded-full font-medium">
          Waiting
        </span>
      )}
      <div className={clsx('w-16 h-16 mx-auto mb-3 rounded-xl flex items-center justify-center text-white font-bold text-2xl', colors[color])}>
        {avatar}
      </div>
      <p className="font-semibold text-surface-900 dark:text-surface-100">{name}</p>
      <p className={clsx('text-sm mt-1 flex items-center justify-center gap-1', statusColors[status])}>
        {status === 'joined' && <Check className="w-3.5 h-3.5" />}
        {status === 'waiting' && <Clock className="w-3.5 h-3.5 animate-spin" />}
        {status === 'creator' && <LinkIcon className="w-3.5 h-3.5" />}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </p>
    </div>
  )
}

interface PromptPreviewProps {
  label: string
  left: string
  right: string
  avg?: number
  color: 'brand' | 'purple'
}

function PromptPreview({ label, left, right, avg, color }: PromptPreviewProps) {
  const colors = {
    brand: 'from-brand-500 to-brand-600',
    purple: 'from-purple-500 to-purple-600',
  }

  return (
    <div className="p-4 bg-surface-50 dark:bg-surface-900/50 rounded-xl">
      <p className="font-semibold text-surface-900 dark:text-surface-100 mb-3">{label}</p>
      <div className="flex items-center justify-between text-sm text-surface-500 mb-2">
        <span>{left}</span>
        <span>{right}</span>
      </div>
      <div className="h-2 bg-surface-200 dark:bg-surface-700 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r rounded-full" style={{ background: colors[color] }} />
      </div>
      <p className="text-xs text-surface-500 mt-1 text-center">Crowd average: {avg ?? 50}</p>
    </div>
  )
}

function formatTimeRemaining(expiresAt: string) {
  const now = new Date()
  const expires = new Date(expiresAt)
  const diff = expires.getTime() - now.getTime()
  
  if (diff <= 0) return 'Expired'
  
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}